import { bindable } from "aurelia-templating";
import { autoinject, transient } from "aurelia-dependency-injection";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { Messages } from "../../../configuration/event-messages.constants";
import { computedFrom } from "aurelia-binding";
import { NotifyPropertyChangedBase, Reflection } from "../../../utilities/reflection";
import * as Enumerable from "linq";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";

export type MonthYearType = { id: number, month: number, year: number, isSelected: boolean };

@autoinject
@transient()
export class CompMonthPicker extends NotifyPropertyChangedBase {

    private _subscriptionToDocumentClickEvent: Subscription;
    private _snapshot: { isMonthRangeShowing: boolean, months: MonthYearType[], minMonthRangeId: number, maxMonthRangeId: number };

    public readOnlyValue: any;
    public isVisible: boolean = false;
    public isMonthRangeShowing: boolean = true;
    public monthNames = {
        1: 'Jan',
        2: 'Feb',
        3: 'Mar',
        4: 'Apr',
        5: 'May',
        6: 'Jun',
        7: 'Jul',
        8: 'Aug',
        9: 'Sep',
        10: 'Oct',
        11: 'Nov',
        12: 'Dec'
    };
    public minMonthRangeId: number;
    public maxMonthRangeId: number;

    public get maxMonthRange(): MonthYearType {
        return this.months.filter(x => x.id == this.maxMonthRangeId)[0];
    }

    public get minMonthRange(): MonthYearType {
        return this.months.filter(x => x.id == this.minMonthRangeId)[0];
    }

    @computedFrom(
        Reflection.getPropertyPath<CompMonthPicker>(x => x.minMonthRangeId),
        Reflection.getPropertyPath<CompMonthPicker>(x => x.changes.months),
        Reflection.getPropertyPath<CompMonthPicker>(x => x.months)
    )
    public get maxMonths(): MonthYearType[] {
        return this.months && this.minMonthRange ? this.months.filter(x => {
            return (x.month > this.minMonthRange.month && x.year == this.minMonthRange.year) || x.year > this.minMonthRange.year
        }) : [];
    }

    @computedFrom(
        Reflection.getPropertyPath<CompMonthPicker>(x => x.minMonthRangeId),
        Reflection.getPropertyPath<CompMonthPicker>(x => x.maxMonthRangeId),
        Reflection.getPropertyPath<CompMonthPicker>(x => x.changes.months),
        Reflection.getPropertyPath<CompMonthPicker>(x => x.months),
        Reflection.getPropertyPath<CompMonthPicker>(x => x.isMonthRangeShowing),
        Reflection.getPropertyPath<CompMonthPicker>(x => x.isVisible)
    )
    public get selectedMonthsStr(): string {
        return this.isMonthRangeShowing
            ? (this.minMonthRange && this.maxMonthRange
                ? `${this.monthNames[this.minMonthRange.month]} ${this.minMonthRange.year} - ${this.monthNames[this.maxMonthRange.month]} ${this.maxMonthRange.year}`
                : '')
            : `${this.months.filter(x => x.isSelected).map(x => {
                return this.monthNames[x.month] + " " + x.year
            }).join(", ")}`;
    }

    @computedFrom(
        Reflection.getPropertyPath<CompMonthPicker>(x => x.minMonthRangeId),
        Reflection.getPropertyPath<CompMonthPicker>(x => x.maxMonthRangeId),
        Reflection.getPropertyPath<CompMonthPicker>(x => x.changes.months),
        Reflection.getPropertyPath<CompMonthPicker>(x => x.months),
        Reflection.getPropertyPath<CompMonthPicker>(x => x.isMonthRangeShowing),
        Reflection.getPropertyPath<CompMonthPicker>(x => x.isVisible)
    )
    public get canApplyChanges(): boolean {
        return this.isMonthRangeShowing
            ? this.minMonthRange != null && this.maxMonthRange != null :
            this.months.some(x => x.isSelected)
    }

    @bindable
    public months: MonthYearType[] = [];

    @bindable
    public placeholder: string = "";

    @bindable
    public readonly: boolean;

    @bindable
    public disabled: boolean;

    @bindable
    public label: string;

    @bindable
    public info: string = "";

    @bindable
    public infoBottom: string = "";

    @bindable
    public showPickerIcon: boolean = true;

    constructor(private eventAggregator: EventAggregator, private element: Element) {
        super();
    }

    public attached(): void {
        this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closeDropdown);
    }

    detached() {
        if (this._subscriptionToDocumentClickEvent) {
            this._subscriptionToDocumentClickEvent.dispose();
        }
    }

    monthsChanged() {
        if (this.months) {
            if (this.isMonthRangeShowing) {
                this.minMonthRangeId = Enumerable.from(this.months)
                    .where(x => x.isSelected)
                    .orderBy(x => x.year)
                    .thenBy(x => x.month)
                    .firstOrDefault()?.id;

                this.maxMonthRangeId = Enumerable.from(this.months)
                    .where(x => x.isSelected)
                    .orderByDescending(x => x.year)
                    .thenByDescending(x => x.month)
                    .firstOrDefault()?.id;


                this.onPropertyChanged<CompMonthPicker>(x => x.months);
            }
        }
    }

    public showMonthSelection(event) {
        this.isVisible = true;
        this._snapshot = {
            isMonthRangeShowing: this.isMonthRangeShowing,
            months: CloneHelper.cloneObject(this.months),
            minMonthRangeId: this.minMonthRangeId,
            maxMonthRangeId: this.maxMonthRangeId
        };
        event.stopPropagation();
        event.preventDefault();
    }

    public switchToMonthRange() {
        this.isMonthRangeShowing = true;
    }

    public switchToSpecificMonthDropdown() {
        this.isMonthRangeShowing = false;
    }

    public applyChanges() {
        if(!this.canApplyChanges){
            return;
        }
        this.isVisible = false;
        //Make sure that if month range was selected, we sync the isSelected flag in months based on min/max months range
        if (this.isMonthRangeShowing) {
            this.onMonthRangeChanged();
        }
        const specificMonthsSelectedEvent = new CustomEvent("months-selected-changed", { bubbles: true });
        this.element.dispatchEvent(specificMonthsSelectedEvent);
    }

    public closeDropdown = (x) => {
        if (x && x.source == "document" && this.isVisible) {
            this.isVisible = false;
            //Reset data is the dropdown is closed without the changes applied
            this.isMonthRangeShowing = this._snapshot.isMonthRangeShowing;
            this.months.forEach(month => {
                month.isSelected = this._snapshot.months.filter(x => x.id == month.id)[0].isSelected;
            });
            this.minMonthRangeId = this._snapshot.minMonthRangeId;
            this.maxMonthRangeId = this._snapshot.maxMonthRangeId;
        }
    }

    public clear($event) {
        this.months.forEach(x => x.isSelected = false);
        this.minMonthRangeId = null;
        this.maxMonthRangeId = null;
        $event.stopPropagation();
        this.onMonthsSelectedChanged();
    }

    public onMonthRangeChanged() {

        const min = this.minMonthRange;
        const max = this.maxMonthRange;
        this.months.forEach(x => {
            x.isSelected = (min && (x.month >= min.month && x.year == min.year || x.year > min.year)) &&
                (max && (x.month <= max.month && x.year == max.year || x.year < max.year));
        });
        this.onMonthsSelectedChanged();
    }

    public onMonthSelectionChanged() {
        this.onMonthsSelectedChanged();
    }

    public onMonthsSelectedChanged() {
        this.onPropertyChanged<CompMonthPicker>(x => x.months);
    }

    public displayMonthFunc = (item: MonthYearType) => {
        return this.monthNames[item.month] + " " + item.year;
    }
}

